/**
 * PRNTS Theme JavaScript
 * Zie: ARCHITECTUUR.md Sectie 5
 * 
 * Module bundel met:
 * - Icons (centraal SVG icon systeem)
 * - Header (sticky, mobile menu, search)
 * - Cart (drawer, add to cart, update)
 * - Toast notifications
 * - Utility functions
 */

(function() {
  'use strict';

  // ==========================================================================
  // Centraal Icon Systeem
  // ==========================================================================
  
  /**
   * Centraal icon object - alle SVG icons op één plek
   * Gebruik: PRNTS.Icons.get('close', 16)
   */
  const Icons = {
    // Icon paths - alleen de inhoud van de SVG
    paths: {
      close: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
      check: '<polyline points="20 6 9 17 4 12"/>',
      'alert-circle': '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
      info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
      heart: '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
      'heart-filled': '<path fill="currentColor" stroke="none" d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
      trash: '<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>',
      plus: '<path d="M5 12h14"/><path d="M12 5v14"/>',
      minus: '<path d="M5 12h14"/>',
      'chevron-down': '<path d="m6 9 6 6 6-6"/>',
      'chevron-up': '<path d="m18 15-6-6-6 6"/>',
      'chevron-left': '<path d="m15 18-6-6 6-6"/>',
      'chevron-right': '<path d="m9 18 6-6-6-6"/>',
      cart: '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>',
      search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
      user: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
      menu: '<line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/>',
      'external-link': '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/>',
      share: '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>',
      star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
      'star-filled': '<polygon fill="currentColor" stroke="none" points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    },
    
    /**
     * Genereer een SVG icon
     * @param {string} name - Icon naam
     * @param {number} size - Icon grootte in pixels
     * @param {string} className - Extra CSS class
     * @returns {string} Complete SVG HTML
     */
    get(name, size = 24, className = '') {
      const path = this.paths[name];
      if (!path) {
        console.warn(`Icon "${name}" niet gevonden`);
        return '';
      }
      
      const classes = `icon icon--${name}${className ? ' ' + className : ''}`;
      return `<svg class="${classes}" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
    }
  };

  // ==========================================================================
  // Utilities
  // ==========================================================================
  
  const PRNTS = window.PRNTS || {};
  
  // Expose Icons globally
  PRNTS.Icons = Icons;
  
  /**
   * Debounce function calls
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Wait time in ms
   * @returns {Function}
   */
  function debounce(fn, wait = 300) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
  
  /**
   * Throttle function calls
   * @param {Function} fn - Function to throttle
   * @param {number} limit - Limit in ms
   * @returns {Function}
   */
  function throttle(fn, limit = 100) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        fn.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
  
  /**
   * Fetch with error handling
   * @param {string} url - URL to fetch
   * @param {Object} options - Fetch options
   * @returns {Promise}
   */
  async function fetchJSON(url, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    };
    
    const response = await fetch(url, { ...defaultOptions, ...options });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }
  
  /**
   * Format money according to Shopify settings
   * @param {number} cents - Amount in cents
   * @returns {string}
   */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `€${amount.replace('.', ',')}`;
  }
  
  /**
   * Trap focus within element
   * @param {HTMLElement} element - Element to trap focus in
   */
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    element.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    });
    
    firstFocusable?.focus();
  }
  
  // ==========================================================================
  // Toast Notifications
  // ==========================================================================
  
  const Toast = {
    container: null,
    
    init() {
      this.container = document.getElementById('notification-area');
    },
    
    /**
     * Show a toast notification
     * @param {Object} options - Toast options
     * @param {string} options.message - Message text
     * @param {string} options.type - Type: success, error, warning, info
     * @param {number} options.duration - Duration in ms (0 = persistent)
     */
    show({ message, type = 'info', title = '', duration = 4000 }) {
      if (!this.container) this.init();
      
      const toast = document.createElement('div');
      toast.className = `toast toast--${type}`;
      toast.setAttribute('role', 'alert');
      
      const iconMap = {
        success: 'check',
        error: 'alert',
        warning: 'alert',
        info: 'info',
      };
      
      toast.innerHTML = `
        <div class="toast__icon">
          ${this.getIcon(iconMap[type] || 'info')}
        </div>
        <div class="toast__content">
          ${title ? `<div class="toast__title">${title}</div>` : ''}
          <div class="toast__message">${message}</div>
        </div>
        <button type="button" class="toast__close" aria-label="Sluiten">
          ${this.getIcon('close')}
        </button>
      `;
      
      toast.querySelector('.toast__close').addEventListener('click', () => {
        this.hide(toast);
      });
      
      this.container.appendChild(toast);
      
      if (duration > 0) {
        setTimeout(() => this.hide(toast), duration);
      }
      
      return toast;
    },
    
    hide(toast) {
      toast.style.animation = 'toast-out 0.3s ease-out forwards';
      setTimeout(() => toast.remove(), 300);
    },
    
    getIcon(name) {
      // Gebruik centraal icon systeem
      const iconMap = {
        check: 'check',
        alert: 'alert-circle',
        info: 'info',
        close: 'close'
      };
      return Icons.get(iconMap[name] || name, name === 'close' ? 16 : 20);
    },
    
    success(message, title = '') {
      return this.show({ message, title, type: 'success' });
    },
    
    error(message, title = '') {
      return this.show({ message, title, type: 'error' });
    },
    
    warning(message, title = '') {
      return this.show({ message, title, type: 'warning' });
    },
  };
  
  // ==========================================================================
  // Header Module
  // ==========================================================================
  
  const Header = {
    header: null,
    mobileMenu: null,
    searchDrawer: null,
    lastScrollY: 0,
    isHidden: false,
    
    init() {
      this.header = document.querySelector('[data-header]');
      this.mobileMenu = document.querySelector('[data-mobile-menu]');
      this.searchDrawer = document.querySelector('[data-search-drawer]');
      
      if (!this.header) return;
      
      this.bindEvents();
      this.initStickyHeader();
    },
    
    bindEvents() {
      // Mobile menu toggle
      document.querySelectorAll('[data-menu-toggle]').forEach(btn => {
        btn.addEventListener('click', () => this.toggleMobileMenu());
      });
      
      document.querySelectorAll('[data-menu-close]').forEach(btn => {
        btn.addEventListener('click', () => this.closeMobileMenu());
      });
      
      // Mobile submenu toggles
      document.querySelectorAll('[data-submenu-toggle]').forEach(btn => {
        btn.addEventListener('click', () => this.toggleSubmenu(btn));
      });
      
      document.querySelectorAll('[data-submenu-back]').forEach(btn => {
        btn.addEventListener('click', () => this.closeSubmenu(btn));
      });
      
      // Search toggle
      document.querySelectorAll('[data-search-toggle]').forEach(btn => {
        btn.addEventListener('click', () => this.toggleSearch());
      });
      
      document.querySelectorAll('[data-search-close]').forEach(btn => {
        btn.addEventListener('click', () => this.closeSearch());
      });
      
      // Desktop dropdown hover/focus
      document.querySelectorAll('.header__menu-item--has-dropdown').forEach(item => {
        const btn = item.querySelector('.header__menu-link');
        
        item.addEventListener('mouseenter', () => {
          btn?.setAttribute('aria-expanded', 'true');
        });
        
        item.addEventListener('mouseleave', () => {
          btn?.setAttribute('aria-expanded', 'false');
        });
        
        btn?.addEventListener('focus', () => {
          btn.setAttribute('aria-expanded', 'true');
        });
      });
      
      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeMobileMenu();
          this.closeSearch();
        }
      });
    },
    
    initStickyHeader() {
      if (!this.header.classList.contains('header--sticky')) return;
      
      const onScroll = throttle(() => {
        const scrollY = window.scrollY;
        const headerHeight = this.header.offsetHeight;
        
        // Add scrolled class after scrolling past header
        this.header.classList.toggle('is-scrolled', scrollY > headerHeight);
        
        // Hide header on scroll down, show on scroll up
        if (scrollY > this.lastScrollY && scrollY > headerHeight * 2) {
          if (!this.isHidden) {
            this.header.classList.add('is-hidden');
            this.isHidden = true;
          }
        } else {
          if (this.isHidden) {
            this.header.classList.remove('is-hidden');
            this.isHidden = false;
          }
        }
        
        this.lastScrollY = scrollY;
      }, 100);
      
      window.addEventListener('scroll', onScroll, { passive: true });
    },
    
    toggleMobileMenu() {
      const isOpen = this.mobileMenu?.getAttribute('aria-hidden') === 'false';
      
      if (isOpen) {
        this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    },
    
    openMobileMenu() {
      if (!this.mobileMenu) return;
      
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      const toggle = document.querySelector('[data-menu-toggle]');
      toggle?.setAttribute('aria-expanded', 'true');
      
      trapFocus(this.mobileMenu);
    },
    
    closeMobileMenu() {
      if (!this.mobileMenu) return;
      
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      const toggle = document.querySelector('[data-menu-toggle]');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.focus();
      
      // Reset any open submenus
      this.mobileMenu.querySelectorAll('[data-submenu]').forEach(submenu => {
        submenu.classList.remove('is-open');
        submenu.hidden = true;
        submenu.previousElementSibling?.setAttribute('aria-expanded', 'false');
      });
    },
    
    toggleSubmenu(btn) {
      const submenu = btn.nextElementSibling;
      if (!submenu) return;

      const isOpen = submenu.classList.contains('is-open');

      // Close other open submenus
      if (!isOpen) {
        this.mobileMenu.querySelectorAll('[data-submenu].is-open').forEach(other => {
          other.classList.remove('is-open');
          other.previousElementSibling?.setAttribute('aria-expanded', 'false');
          setTimeout(() => { other.hidden = true; }, 300);
        });
      }

      if (isOpen) {
        submenu.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        setTimeout(() => { submenu.hidden = true; }, 300);
      } else {
        submenu.hidden = false;
        // Force reflow for animation
        submenu.offsetHeight;
        submenu.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }
    },

    closeSubmenu(btn) {
      const submenu = btn.closest('[data-submenu]');
      const toggle = submenu?.previousElementSibling;

      if (submenu) {
        submenu.classList.remove('is-open');
        toggle?.setAttribute('aria-expanded', 'false');
        setTimeout(() => {
          submenu.hidden = true;
          toggle?.focus();
        }, 300);
      }
    },
    
    toggleSearch() {
      const isOpen = this.searchDrawer?.getAttribute('aria-hidden') === 'false';
      
      if (isOpen) {
        this.closeSearch();
      } else {
        this.openSearch();
      }
    },
    
    openSearch() {
      if (!this.searchDrawer) return;
      
      this.searchDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      const toggle = document.querySelector('[data-search-toggle]');
      toggle?.setAttribute('aria-expanded', 'true');
      
      const input = this.searchDrawer.querySelector('input');
      input?.focus();
    },
    
    closeSearch() {
      if (!this.searchDrawer) return;
      
      this.searchDrawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      const toggle = document.querySelector('[data-search-toggle]');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.focus();
    },
  };
  
  // ==========================================================================
  // Cart Module
  // ==========================================================================
  
  const Cart = {
    drawer: null,
    items: [],
    
    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.bindEvents();
      this.updateCartCount(PRNTS.cart?.item_count || 0);
    },
    
    bindEvents() {
      // Cart toggle
      document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleDrawer();
        });
      });
      
      document.querySelectorAll('[data-cart-close]').forEach(btn => {
        btn.addEventListener('click', () => this.closeDrawer());
      });
      
      // Add to cart forms (skip forms that handle their own submission)
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form || form.hasAttribute('data-customizer-form')) return;

        if (PRNTS.settings?.cartType === 'drawer') {
          e.preventDefault();
          this.addToCart(form);
        }
      });
      
      // Quick add buttons
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (btn) {
          e.preventDefault();
          const variantId = btn.dataset.variantId;
          this.addItem(variantId, 1);
        }
      });
      
      // Quantity changes in cart
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cart-quantity]');
        if (btn) {
          const key = btn.dataset.lineKey;
          const action = btn.dataset.cartQuantity;
          const qtyEl = btn.parentElement.querySelector('[data-quantity-value]') || btn.parentElement.querySelector('input');
          let quantity = parseInt(qtyEl?.textContent || qtyEl?.value || 1);
          
          if (action === 'plus') quantity++;
          if (action === 'minus') quantity--;
          
          this.updateItem(key, Math.max(0, quantity));
        }
      });
      
      // Remove item
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cart-remove]');
        if (btn) {
          const key = btn.dataset.lineKey;
          this.updateItem(key, 0);
        }
      });
      
      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeDrawer();
        }
      });
    },
    
    toggleDrawer() {
      if (PRNTS.settings?.cartType !== 'drawer') {
        window.location.href = PRNTS.routes?.cart || '/cart';
        return;
      }
      
      const isOpen = this.drawer?.getAttribute('aria-hidden') === 'false';
      
      if (isOpen) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    },
    
    openDrawer() {
      if (!this.drawer) return;
      
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      const toggle = document.querySelector('[data-cart-toggle]');
      toggle?.setAttribute('aria-expanded', 'true');
      
      trapFocus(this.drawer);
      this.refreshDrawer();
    },
    
    closeDrawer() {
      if (!this.drawer) return;
      
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      const toggle = document.querySelector('[data-cart-toggle]');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.focus();
    },
    
    async addToCart(form) {
      const formData = new FormData(form);
      const submitBtn = form.querySelector('[type="submit"]');
      
      // Check variant ID
      const variantId = formData.get('id');
      if (!variantId) {
        Toast.error('Geen product variant geselecteerd');
        return;
      }

      submitBtn?.classList.add('is-loading');
      submitBtn?.setAttribute('disabled', 'disabled');
      
      try {
        // Convert FormData to URL encoded string for reliable JSON response
        const body = new URLSearchParams(formData).toString();
        
        const response = await fetch(PRNTS.routes?.cart_add || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json',
          },
          body: body,
        });
        
        // Parse JSON response
        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          throw new Error('Server gaf geen geldig antwoord');
        }
        
        if (response.ok) {
          await this.refreshCart();
          this.openDrawer();
          Toast.success('Product toegevoegd aan winkelwagen');
        } else {
          throw new Error(data.description || 'Er ging iets mis');
        }
      } catch (error) {
        Toast.error(error.message || 'Er ging iets mis bij toevoegen aan winkelwagen');
      } finally {
        submitBtn?.classList.remove('is-loading');
        submitBtn?.removeAttribute('disabled');
      }
    },
    
    async addItem(variantId, quantity = 1) {
      if (!variantId) {
        Toast.error('Geen product variant geselecteerd');
        return;
      }
      
      try {
        const response = await fetch(PRNTS.routes?.cart_add || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }],
          }),
        });
        
        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server returned invalid response');
        }
        
        const data = await response.json();
        
        if (response.ok) {
          await this.refreshCart();
          this.openDrawer();
          Toast.success('Product toegevoegd aan winkelwagen');
        } else {
          throw new Error(data.description || 'Er ging iets mis');
        }
      } catch (error) {
        Toast.error(error.message);
      }
    },
    
    async updateItem(key, quantity) {
      const lineItem = document.querySelector(`[data-line-key="${key}"]`);
      lineItem?.classList.add('is-loading');
      
      try {
        const response = await fetch(PRNTS.routes?.cart_change || '/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ id: key, quantity }),
        });
        
        let cart;
        try {
          cart = await response.json();
        } catch (parseError) {
          throw new Error('Server gaf geen geldig antwoord');
        }
        
        if (response.ok) {
          PRNTS.cart = cart;
          this.updateCartCount(cart.item_count);
          this.refreshDrawer();
          
          if (quantity === 0) {
            Toast.success('Product verwijderd');
          }
        } else {
          throw new Error(cart.description || 'Er ging iets mis');
        }
      } catch (error) {
        Toast.error(error.message);
        lineItem?.classList.remove('is-loading');
      }
    },
    
    async refreshCart() {
      try {
        const response = await fetch(PRNTS.routes?.cart || '/cart.js', {
          headers: { 'Accept': 'application/json' }
        });
        const cart = await response.json();
        PRNTS.cart = cart;
        this.updateCartCount(cart.item_count);
        return cart;
      } catch (error) {
        // Silently fail - cart will refresh on next page load
      }
    },
    
    async refreshDrawer() {
      // Re-query drawer in case it was re-rendered or not found at init
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;

      try {
        // Include preview_theme_id if previewing unpublished theme
        let drawerUrl = '/?section_id=cart-drawer';
        const previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (previewId) drawerUrl += '&preview_theme_id=' + previewId;

        const response = await fetch(drawerUrl);
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Try multiple selectors for compatibility
        const newDrawerContent = doc.querySelector('[data-cart-drawer-content]')
                              || doc.querySelector('.cart-drawer__content');
        const currentDrawerContent = this.drawer.querySelector('[data-cart-drawer-content]')
                                  || this.drawer.querySelector('.cart-drawer__content');

        if (newDrawerContent && currentDrawerContent) {
          currentDrawerContent.innerHTML = newDrawerContent.innerHTML;
        } else {
          // Fallback: try to replace entire drawer
          const newDrawer = doc.querySelector('[data-cart-drawer]');
          if (newDrawer && this.drawer) {
            this.drawer.innerHTML = newDrawer.innerHTML;
          }
        }

        // JS-based property injection fallback
        try {
          const cartRes = await fetch('/cart.js');
          const cartJson = await cartRes.json();
          const drawerItems = document.querySelectorAll('.cart-drawer__item[data-line-key]');
          drawerItems.forEach(itemEl => {
            const lineKey = itemEl.getAttribute('data-line-key');
            const cartItem = cartJson.items.find(ci => ci.key === lineKey);
            if (!cartItem) return;
            const existingProps = itemEl.querySelector('.cart-drawer__item-properties');
            const existingCount = existingProps ? existingProps.querySelectorAll('.cart-drawer__item-property').length : 0;
            const visibleProps = Object.entries(cartItem.properties || {}).filter(([k, v]) => !k.startsWith('_') && v && v !== '');
            if (visibleProps.length > 0 && existingCount === 0) {
              let container = existingProps || document.createElement('div');
              if (!existingProps) {
                container.className = 'cart-drawer__item-properties';
                const details = itemEl.querySelector('.cart-drawer__item-details');
                const price = itemEl.querySelector('.cart-drawer__item-price');
                if (details && price) details.insertBefore(container, price);
                else if (details) details.appendChild(container);
              }
              container.innerHTML = '';
              visibleProps.forEach(([k, v]) => {
                const p = document.createElement('p');
                p.className = 'cart-drawer__item-property';
                const span = document.createElement('span');
                span.textContent = k + ':';
                p.appendChild(span);
                p.appendChild(document.createTextNode(' ' + v));
                container.appendChild(p);
              });
            }
          });
        } catch (propErr) {
          // Property injection is non-critical
        }
      } catch (error) {
        // Drawer refresh failed - will update on next interaction
      }
    },
    
    updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.classList.toggle('is-hidden', count === 0);
      });
      
      // Update aria-label
      document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
        const label = PRNTS.strings?.cartTitle || 'Winkelwagen';
        btn.setAttribute('aria-label', `${label} (${count})`);
      });
    },
  };
  
  // ==========================================================================
  // Wishlist Module
  // ==========================================================================

  const Wishlist = {
    STORAGE_KEY: 'prnts_wishlist',
    items: [],

    init() {
      this.items = this.getItems();
      this.bindEvents();
      this.syncUI();
    },

    getItems() {
      try {
        return JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
      } catch { return []; }
    },

    saveItems() {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.items));
    },

    toggle(handle, data) {
      const index = this.items.findIndex(i => i.handle === handle);
      if (index > -1) {
        this.items.splice(index, 1);
        Toast.show({ message: 'Verwijderd van verlanglijst', type: 'info' });
      } else {
        this.items.push({ handle, ...data, addedAt: Date.now() });
        Toast.success('Toegevoegd aan verlanglijst');
      }
      this.saveItems();
      this.syncUI();
    },

    isInWishlist(handle) {
      return this.items.some(i => i.handle === handle);
    },

    bindEvents() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wishlist-button]');
        if (!btn) return;
        e.preventDefault();

        const handle = btn.dataset.productHandle;
        const data = {
          title: btn.dataset.productTitle,
          price: btn.dataset.productPrice,
          image: btn.dataset.productImage,
          url: btn.dataset.productUrl,
        };
        this.toggle(handle, data);
      });
    },

    syncUI() {
      // Update all wishlist buttons
      document.querySelectorAll('[data-wishlist-button]').forEach(btn => {
        const handle = btn.dataset.productHandle;
        const active = this.isInWishlist(handle);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      // Update header count badge
      const count = this.items.length;
      document.querySelectorAll('[data-wishlist-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },
  };

  // ==========================================================================
  // Predictive Search
  // ==========================================================================

  const PredictiveSearch = {
    input: null,
    resultsContainer: null,
    controller: null,

    init() {
      this.input = document.querySelector('[data-predictive-search-input]');
      this.resultsContainer = document.querySelector('[data-predictive-search-results]');

      if (!this.input || !this.resultsContainer) return;

      this.bindEvents();
    },

    bindEvents() {
      const debouncedSearch = debounce((query) => this.search(query), 300);

      this.input.addEventListener('input', (e) => {
        const query = e.target.value.trim();

        if (query.length >= 2) {
          debouncedSearch(query);
        } else {
          this.clearResults();
        }
      });
    },

    async search(query) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const url = `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,article&resources[limit]=4&section_id=predictive-search`;

      try {
        this.resultsContainer.innerHTML = '<div class="predictive-search__loading"><p>Zoeken...</p></div>';

        const response = await fetch(url, { signal: this.controller.signal });
        const text = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const results = doc.getElementById('predictive-search-results');

        if (results) {
          this.resultsContainer.innerHTML = results.innerHTML;
        } else {
          this.resultsContainer.innerHTML = '<p class="predictive-search__no-results">Geen resultaten gevonden</p>';
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Predictive search error:', error);
        }
      }
    },

    clearResults() {
      if (this.resultsContainer) this.resultsContainer.innerHTML = '';
    },
  };
  
  // ==========================================================================
  // Product Variants
  // ==========================================================================
  
  const ProductVariants = {
    init() {
      document.querySelectorAll('[data-variant-selector]').forEach(form => {
        this.setupForm(form);
      });
    },
    
    setupForm(form) {
      const selects = form.querySelectorAll('select, input[type="radio"]');
      const productJson = form.querySelector('[data-product-json]');

      if (!productJson) return;

      try {
        const product = JSON.parse(productJson.textContent);

        selects.forEach(select => {
          select.addEventListener('change', () => {
            // Update displayed option text (e.g. "Kleur paars")
            const optionGroup = select.closest('.product__option');
            if (optionGroup) {
              const selectedLabel = optionGroup.querySelector('[data-option-selected]');
              if (selectedLabel) {
                selectedLabel.textContent = select.value;
              }
            }
            this.updateAvailableOptions(form, product);
            this.updateVariant(form, product);
          });
        });

        // Initial check for unavailable options on page load
        this.updateAvailableOptions(form, product);
      } catch (error) {
        console.error('Error parsing product JSON:', error);
      }
    },

    updateAvailableOptions(form, product) {
      const allSelects = form.querySelectorAll('select[data-option-index]');

      allSelects.forEach(select => {
        const optionIndex = parseInt(select.dataset.optionIndex, 10) - 1;

        // Get selected values for all OTHER options
        const otherSelections = {};
        allSelects.forEach(otherSelect => {
          const otherIndex = parseInt(otherSelect.dataset.optionIndex, 10) - 1;
          if (otherIndex !== optionIndex) {
            otherSelections[otherIndex] = otherSelect.value;
          }
        });

        // For each option value, check if any variant exists with that value + current other selections
        Array.from(select.options).forEach(option => {
          const testOptions = { ...otherSelections, [optionIndex]: option.value };
          const hasVariant = product.variants.some(v =>
            Object.entries(testOptions).every(([idx, val]) => v.options[parseInt(idx)] === val) && v.available
          );
          option.disabled = !hasVariant;
        });

        // If current selection is disabled, auto-switch to first available
        if (select.options[select.selectedIndex]?.disabled) {
          const firstAvailable = Array.from(select.options).find(o => !o.disabled);
          if (firstAvailable) {
            select.value = firstAvailable.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    },
    
    updateVariant(form, product) {
      // Get selected options
      const selectedOptions = [];
      form.querySelectorAll('select, input[type="radio"]:checked').forEach(input => {
        selectedOptions.push(input.value);
      });
      
      // Find matching variant
      const variant = product.variants.find(v => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });
      
      if (variant) {
        // Update hidden variant input
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
        }
        
        // Update price display
        this.updatePrice(form, variant);
        
        // Update add to cart button
        this.updateButton(form, variant);
        
        // Update URL
        this.updateURL(variant);
        
        // Dispatch event for other modules
        form.dispatchEvent(new CustomEvent('variant:change', {
          detail: { variant, product },
          bubbles: true,
        }));

        // Update notebook specs based on binding type
        const specsTable = form.closest('.product')?.querySelector('[data-notebook-specs]');
        if (specsTable) {
          const isWiro = variant.title.includes('Ringband');
          specsTable.querySelector('[data-spec="binding"] td').textContent = isWiro ? 'Wire-O-bind' : 'Genaaid gebonden';
          specsTable.querySelector('[data-spec="omslag"]').style.display = isWiro ? '' : 'none';
          specsTable.querySelector('[data-spec="inhoud"] td').textContent = isWiro ? '100 pagina\'s gelinieerd' : '192 pagina\'s gelinieerd';
          specsTable.querySelector('[data-spec="afwerking"]').style.display = isWiro ? 'none' : '';
        }
      }
    },
    
    updatePrice(form, variant) {
      const priceContainer = form.closest('.product')?.querySelector('[data-product-price]');
      if (!priceContainer) return;
      
      const price = priceContainer.querySelector('.price__amount');
      const comparePrice = priceContainer.querySelector('.price__compare');
      
      if (price) {
        price.textContent = formatMoney(variant.price);
      }
      
      if (comparePrice) {
        if (variant.compare_at_price > variant.price) {
          comparePrice.textContent = formatMoney(variant.compare_at_price);
          comparePrice.style.display = '';
        } else {
          comparePrice.style.display = 'none';
        }
      }
    },
    
    updateButton(form, variant) {
      const button = form.querySelector('[type="submit"]');
      const buttonText = button?.querySelector('span');
      
      if (!button) return;
      
      if (!variant.available) {
        button.disabled = true;
        if (buttonText) {
          buttonText.textContent = PRNTS.strings?.soldOut || 'Uitverkocht';
        }
      } else {
        button.disabled = false;
        if (buttonText) {
          buttonText.textContent = PRNTS.strings?.addToCart || 'In winkelwagen';
        }
      }
    },
    
    updateURL(variant) {
      if (!history.replaceState) return;
      
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    },
  };
  
  // ==========================================================================
  // Quantity Selector
  // ==========================================================================
  
  const QuantitySelector = {
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quantity-btn]');
        if (!btn) return;
        
        const wrapper = btn.closest('[data-quantity-selector]');
        const input = wrapper?.querySelector('input');
        if (!input) return;
        
        const action = btn.dataset.quantityBtn;
        let value = parseInt(input.value) || 1;
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 999;
        
        if (action === 'plus' && value < max) {
          value++;
        } else if (action === 'minus' && value > min) {
          value--;
        }
        
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },
  };
  
  // ==========================================================================
  // Image Galleries / Sliders
  // ==========================================================================
  
  const ProductGallery = {
    init() {
      document.querySelectorAll('[data-product-gallery]').forEach(gallery => {
        this.setupGallery(gallery);
      });
    },
    
    setupGallery(gallery) {
      const mainImage = gallery.querySelector('[data-main-image]');
      const thumbnails = gallery.querySelectorAll('[data-thumbnail]');
      
      if (!mainImage || !thumbnails.length) return;
      
      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.src;
          const srcset = thumb.dataset.srcset;
          
          if (src) {
            mainImage.src = src;
          }
          if (srcset) {
            mainImage.srcset = srcset;
          }
          
          thumbnails.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
      
      // Update gallery on variant change
      gallery.closest('.product')?.addEventListener('variant:change', (e) => {
        const variant = e.detail.variant;
        if (variant.featured_image) {
          const imgSrc = variant.featured_image.src;
          // Build srcset from variant image URL (Shopify CDN supports width parameter)
          const baseSrc = imgSrc.split('?')[0];
          const params = imgSrc.includes('?') ? '&' + imgSrc.split('?')[1] : '';
          mainImage.srcset = `${baseSrc}?width=400${params} 400w, ${baseSrc}?width=600${params} 600w, ${baseSrc}?width=800${params} 800w, ${baseSrc}?width=1000${params} 1000w`;
          mainImage.src = `${baseSrc}?width=800${params}`;

          // Activate matching thumbnail (match by src since media ID ≠ image ID)
          const imgFile = baseSrc.split('/').pop();
          thumbnails.forEach(thumb => {
            const isMatch = thumb.dataset.src?.includes(imgFile);
            thumb.classList.toggle('is-active', !!isMatch);
          });
        }
      });
    },
  };
  
  // ==========================================================================
  // Lazy Loading
  // ==========================================================================
  
  const LazyLoad = {
    init() {
      if ('loading' in HTMLImageElement.prototype) {
        // Native lazy loading supported
        document.querySelectorAll('img[loading="lazy"]').forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
        });
      } else {
        // Fallback with Intersection Observer
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
              }
              if (img.dataset.srcset) {
                img.srcset = img.dataset.srcset;
              }
              observer.unobserve(img);
            }
          });
        });
        
        document.querySelectorAll('img[data-src]').forEach(img => {
          observer.observe(img);
        });
      }
    },
  };
  
  // ==========================================================================
  // Initialize
  // ==========================================================================
  
  function init() {
    Toast.init();
    Header.init();
    Cart.init();
    Wishlist.init();
    PredictiveSearch.init();
    ProductVariants.init();
    QuantitySelector.init();
    ProductGallery.init();
    LazyLoad.init();

    // Expose globally
    window.PRNTS = {
      ...PRNTS,
      Toast,
      Header,
      Cart,
      Wishlist,
      debounce,
      throttle,
      formatMoney,
    };
    
    // Theme initialized
  }
  
  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
})();
